import type { GeographicBoundary } from "@/api.types.v2";
import { useSession } from "@/services/auth0/session";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { z } from "zod";

export const createGeographicBoundarySchema = z.object({
  name: z.string().min(1, "Name is required"),
  code: z.string().optional(),
  source: z.string().default("manual"),
  boundary: z.object({
    type: z.literal("Polygon"),
    coordinates: z.array(z.array(z.array(z.number()))),
  }),
  metadata: z.record(z.unknown()).optional(),
  isActive: z.boolean().default(true),
});

export type CreateGeographicBoundaryFormData = z.infer<
  typeof createGeographicBoundarySchema
>;

export const uploadGeoJSONSchema = z.object({
  geoJsonData: z.object({
    type: z.literal("FeatureCollection"),
    features: z.array(
      z.object({
        type: z.literal("Feature"),
        properties: z.record(z.any()),
        geometry: z.union([
          z.object({
            type: z.literal("Polygon"),
            coordinates: z.array(z.array(z.tuple([z.number(), z.number()]))),
          }),
          z.object({
            type: z.literal("Point"),
            coordinates: z.tuple([z.number(), z.number()]),
          }),
        ]),
      })
    ),
  }),
});

export type UploadGeoJSONFormData = z.infer<typeof uploadGeoJSONSchema>;

export const useGeographicBoundaries = () => {
  const { token } = useSession();
  return useQuery({
    queryKey: ["geographicBoundaries"],
    queryFn: async () => {
      const { data } = await axios.get<GeographicBoundary[]>(
        `${import.meta.env.VITE_API_URL}/api/v2/geographic-boundaries`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    enabled: !!token,
  });
};

export const useGeographicBoundaryById = (geographicBoundaryId?: string) => {
  const { token } = useSession();
  return useQuery({
    queryKey: ["geographicBoundaries", geographicBoundaryId],
    queryFn: async () => {
      const { data } = await axios.get<GeographicBoundary>(
        `${import.meta.env.VITE_API_URL}/api/v2/geographic-boundaries/${geographicBoundaryId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    enabled: !!token && !!geographicBoundaryId,
  });
};

export const useCreateGeographicBoundary = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (values: CreateGeographicBoundaryFormData) => {
      const { data } = await axios.post<GeographicBoundary>(
        `${import.meta.env.VITE_API_URL}/api/v2/geographic-boundaries`,
        values,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["geographicBoundaries"],
      });
      onSuccess?.();
    },
    onError: (error) => {
      console.error("Error creating geographic boundary:", error);
      onError?.(error);
    },
  });
};

export const useUpdateGeographicBoundary = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      values: CreateGeographicBoundaryFormData & { id: string }
    ) => {
      const { data } = await axios.patch<GeographicBoundary>(
        `${import.meta.env.VITE_API_URL}/api/v2/geographic-boundaries/${values.id}`,
        values,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["geographicBoundaries"],
      });
      onSuccess?.();
    },
    onError: (error) => {
      console.error("Error updating geographic boundary:", error);
      onError?.(error);
    },
  });
};

export const useDeleteGeographicBoundary = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (geographicBoundaryId: string) => {
      await axios.delete(
        `${import.meta.env.VITE_API_URL}/api/v2/geographic-boundaries/${geographicBoundaryId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["geographicBoundaries"],
      });
      onSuccess?.();
    },
    onError: (error) => {
      console.error("Error deleting geographic boundary:", error);
      onError?.(error);
    },
  });
};

export const useUploadGeoJSON = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (result: {
    message: string;
    createdBoundaries: GeographicBoundary[];
    totalFeatures: number;
  }) => void;
  onError?: (error: Error) => void;
}) => {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (values: UploadGeoJSONFormData) => {
      const { data } = await axios.post<{
        message: string;
        createdBoundaries: GeographicBoundary[];
        totalFeatures: number;
      }>(
        `${import.meta.env.VITE_API_URL}/api/v2/geographic-boundaries/upload`,
        values,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({
        queryKey: ["geographicBoundaries"],
      });
      onSuccess?.(result);
    },
    onError: (error) => {
      console.error("Error uploading GeoJSON:", error);
      onError?.(error);
    },
  });
};

export const useFetchFromOSM = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (result: {
    message: string;
    boundary: GeographicBoundary;
    osmData: Record<string, unknown>;
  }) => void;
  onError?: (error: Error) => void;
}) => {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (relationId: string) => {
      const { data } = await axios.post<{
        message: string;
        boundary: GeographicBoundary;
        osmData: Record<string, unknown>;
      }>(
        `${import.meta.env.VITE_API_URL}/api/v2/geographic-boundaries/osm/${relationId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({
        queryKey: ["geographicBoundaries"],
      });
      onSuccess?.(result);
    },
    onError: (error) => {
      console.error("Error fetching from OpenStreetMap:", error);
      onError?.(error);
    },
  });
};
